import { Link } from 'react-router-dom';
import BookCover from '@/components/base/BookCover';
import { useCollection } from '@/hooks/useCollection';
import type { Book } from '@/lib/types';

export default function BooksPreview() {
  const { rows: books, loading, error, reload } = useCollection<Book>('books', 'sort_order', true);
  const published = books.filter((b) => b.is_published !== false).slice(0, 3);

  if (!loading && !error && published.length === 0) return null;

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">
              TGMCI Publications
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight">
              Books That Transform Lives
            </h2>
            <div className="w-12 h-1 bg-accent-500 mt-5" />
          </div>
          <Link to="/books" className="btn-outline shrink-0 whitespace-nowrap">
            View All Books
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </div>

        {loading ? (
          <p className="text-center text-foreground-400 py-10">
            <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading books...
          </p>
        ) : error ? (
          <div className="text-center py-10">
            <p className="text-red-600 text-sm mb-3">Could not load the books.</p>
            <button type="button" onClick={reload} className="btn-outline">Retry</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
            {published.map((book) => (
              <article key={book.id} className="group flex flex-col">
                <div className="h-80 rounded-xl overflow-hidden border border-background-200 group-hover:-translate-y-1 transition-transform">
                  <BookCover
                    title={book.title}
                    subtitle={book.subtitle}
                    author={book.author}
                    image={book.cover_image}
                  />
                </div>
                <h3 className="font-heading font-semibold text-base text-foreground-900 leading-tight mt-5 mb-1 line-clamp-2">
                  {book.title}
                </h3>
                {book.subtitle && (
                  <p className="text-accent-600 text-xs font-label font-medium mb-3 line-clamp-2">{book.subtitle}</p>
                )}
                <a
                  href={book.buy_url || '#'}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="mt-auto btn-accent self-start whitespace-nowrap text-sm px-4 py-2.5"
                >
                  <i className="ri-shopping-bag-3-line" aria-hidden="true" />
                  Buy Now
                </a>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}